// Admin service - Real API calls to backend
import { apiClient } from './api';
import { API_ENDPOINTS } from '../utils/constants';

// Dashboard statistics
export const getDashboardStats = async () => {
  try {
    // Build query parameters for each request
    const employeeParams = new URLSearchParams();
    employeeParams.append('role', 'employee');
    
    const employerParams = new URLSearchParams();
    employerParams.append('role', 'employer');
    
    const pendingEmployeeParams = new URLSearchParams();
    pendingEmployeeParams.append('role', 'employee');
    pendingEmployeeParams.append('is_verified_by_admin', 'false');
    
    const pendingEmployerParams = new URLSearchParams();
    pendingEmployerParams.append('role', 'employer');
    pendingEmployerParams.append('is_verified_by_admin', 'false');
    
    // Job status filters
    const allJobsParams = new URLSearchParams();
    const pendingJobsParams = new URLSearchParams();
    pendingJobsParams.append('status', 'open');
    const completedJobsParams = new URLSearchParams();
    completedJobsParams.append('status', 'done');
    
    // Fetch all data in parallel
    const [
      employees,
      employers,
      pendingEmployees,
      pendingEmployers,
      allJobs,
      pendingJobs,
      completedJobs,
      jobRoles
    ] = await Promise.all([
      apiClient.get(`/users/?${employeeParams.toString()}`),
      apiClient.get(`/users/?${employerParams.toString()}`),
      apiClient.get(`/users/?${pendingEmployeeParams.toString()}`),
      apiClient.get(`/users/?${pendingEmployerParams.toString()}`),
      apiClient.get(`/job-postings/?${allJobsParams.toString()}`),
      apiClient.get(`/job-postings/?${pendingJobsParams.toString()}`),
      apiClient.get(`/job-postings/?${completedJobsParams.toString()}`),
      apiClient.get(API_ENDPOINTS.JOB_ROLES.LIST)
    ]);
    
    return {
      total_employees: Array.isArray(employees) ? employees.length : 0,
      total_employers: Array.isArray(employers) ? employers.length : 0,
      pending_verifications: (Array.isArray(pendingEmployees) ? pendingEmployees.length : 0) + 
                            (Array.isArray(pendingEmployers) ? pendingEmployers.length : 0),
      total_jobs: Array.isArray(allJobs) ? allJobs.length : 0,
      pending_jobs: Array.isArray(pendingJobs) ? pendingJobs.length : 0,
      completed_jobs: Array.isArray(completedJobs) ? completedJobs.length : 0,
      total_job_roles: Array.isArray(jobRoles) ? jobRoles.length : 0
    };
  } catch (error) {
    console.error('Error fetching dashboard stats:', error);
    throw error;
  }
};

// Employee management
export const getEmployees = async (filters = {}) => {
  // Always filter by employee role
  const params = new URLSearchParams();
  params.append('role', 'employee');
  
  if (filters.verification_status === 'pending') {
    params.append('is_verified_by_admin', 'false');
  } else if (filters.verification_status === 'verified') {
    params.append('is_verified_by_admin', 'true');
  }
  
  if (filters.search) {
    params.append('search', filters.search);
  }
  
  if (filters.job_role_id) {
    params.append('job_role_id', filters.job_role_id);
  }
  
  // Build URL with query parameters
  const queryString = params.toString();
  const endpoint = queryString ? `/users/?${queryString}` : '/users/';
  
  return apiClient.get(endpoint);
};

export const getEmployeeDetails = async (id) => {
  return apiClient.get(`/users/${id}/`);
};

export const verifyEmployee = async (id, comment = '') => {
  return apiClient.patch(`/users/${id}/verify_user/`, {
    is_verified_by_admin: true,
    verification_comment: comment
  });
};

export const rejectEmployee = async (id, comment) => {
  return apiClient.patch(`/users/${id}/verify_user/`, {
    is_verified_by_admin: false,
    verification_comment: comment
  });
};

// Employer management
export const getEmployers = async (filters = {}) => {
  // Always filter by employer role
  const params = new URLSearchParams();
  params.append('role', 'employer');
  
  if (filters.verification_status === 'pending') {
    params.append('is_verified_by_admin', 'false');
  } else if (filters.verification_status === 'verified') {
    params.append('is_verified_by_admin', 'true');
  }
  
  if (filters.search) {
    params.append('search', filters.search);
  }
  
  // Build URL with query parameters
  const queryString = params.toString();
  const endpoint = queryString ? `/users/?${queryString}` : '/users/';
  
  return apiClient.get(endpoint);
};

export const getEmployerDetails = async (id) => {
  return apiClient.get(`/users/${id}/`);
};

export const verifyEmployer = async (id, comment = '') => {
  return apiClient.patch(`/users/${id}/verify_user/`, {
    is_verified_by_admin: true,
    verification_comment: comment
  });
};

export const rejectEmployer = async (id, comment) => {
  return apiClient.patch(`/users/${id}/verify_user/`, {
    is_verified_by_admin: false,
    verification_comment: comment
  });
};

// Job management
// Backend status values: open, match_found, accepted, done
export const getAllJobs = async (filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.status && filters.status !== 'all') {
    // Map frontend status to backend status
    const statusMap = {
      'open': 'open',
      'match_found': 'match_found',
      'accepted': 'accepted',
      'done': 'done',
      // Legacy mappings
      'posted': 'open',
      'matched': 'match_found',
      'completed': 'done'
    };
    const backendStatus = statusMap[filters.status] || filters.status;
    params.append('status', backendStatus);
  }
  
  if (filters.employer_id) {
    params.append('employer_id', filters.employer_id);
  }
  
  if (filters.start_date) {
    params.append('start_date', filters.start_date);
  }
  
  if (filters.end_date) {
    params.append('end_date', filters.end_date);
  }
  
  if (filters.job_role_id) {
    params.append('job_role_id', filters.job_role_id);
  }
  
  // Build URL with query parameters
  const queryString = params.toString();
  const endpoint = queryString ? `/job-postings/?${queryString}` : '/job-postings/';
  
  return apiClient.get(endpoint);
};

export const getPendingJobs = async () => {
  // Pending jobs are those with status 'open' or 'match_found'
  // For now, we'll fetch all and filter, or make two requests
  // Backend might need a specific endpoint for this
  const params = new URLSearchParams();
  params.append('status', 'open');
  
  return apiClient.get(`/job-postings/?${params.toString()}`);
};

export const getCompletedJobs = async () => {
  // Completed jobs have status 'done'
  const params = new URLSearchParams();
  params.append('status', 'done');
  
  return apiClient.get(`/job-postings/?${params.toString()}`);
};

export const getJobDetails = async (id) => {
  return apiClient.get(`/job-postings/${id}/`);
};

// Job roles management
export const getJobRoles = async () => {
  return apiClient.get(API_ENDPOINTS.JOB_ROLES.LIST);
};

export const createJobRole = async (data) => {
  return apiClient.post(API_ENDPOINTS.JOB_ROLES.LIST, data);
};

export const updateJobRole = async (id, data) => {
  return apiClient.patch(API_ENDPOINTS.JOB_ROLES.BY_ID(id), data);
};

export const deleteJobRole = async (id) => {
  return apiClient.delete(API_ENDPOINTS.JOB_ROLES.BY_ID(id));
};

// Get job role with mapped documents
export const getJobRoleWithDocuments = async (id) => {
  return apiClient.get(API_ENDPOINTS.JOB_ROLES.DOCUMENTS(id));
};

// Document types management
export const getDocumentTypes = async (filters = {}) => {
  const params = new URLSearchParams();
  
  // Backend expects string values ('true' or 'false') for boolean filters
  if (filters.is_active !== undefined) {
    params.append('is_active', String(filters.is_active));
  }
  
  if (filters.is_required_for_employee !== undefined) {
    params.append('is_required_for_employee', String(filters.is_required_for_employee));
  }
  
  if (filters.is_required_for_employer !== undefined) {
    params.append('is_required_for_employer', String(filters.is_required_for_employer));
  }
  
  if (filters.is_common !== undefined) {
    params.append('is_common', String(filters.is_common));
  }
  
  // Build URL with query parameters
  const queryString = params.toString();
  const endpoint = queryString ? `/document-types/?${queryString}` : '/document-types/';
  
  // If search is needed, fetch all matching filters first, then filter client-side
  if (filters.search) {
    const allDocs = await apiClient.get(endpoint);
    const searchLower = filters.search.toLowerCase();
    return allDocs.filter(doc => 
      doc.name?.toLowerCase().includes(searchLower) ||
      doc.document_key?.toLowerCase().includes(searchLower)
    );
  }
  
  return apiClient.get(endpoint);
};

export const createDocumentType = async (data) => {
  return apiClient.post('/document-types/', data);
};

export const updateDocumentType = async (id, data) => {
  return apiClient.patch(`/document-types/${id}/`, data);
};

export const deleteDocumentType = async (id) => {
  return apiClient.delete(`/document-types/${id}/`);
};

// Document verification
export const verifyDocument = async (documentId, isVerified = true) => {
  return apiClient.patch(`/users/documents/${documentId}/verify/`, {
    is_verified: isVerified
  });
};

